/**
 * Componente de tabla para mostrar y gestionar órdenes de compra en el panel admin
 * Incluye paginación, búsqueda y columnas para acciones futuras
 * Usa @tanstack/react-table para gestión eficiente de datos
 */

import React, { useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper
} from '@tanstack/react-table';
import { RiRefreshLine } from 'react-icons/ri';

/**
 * Formatea fecha para mostrar en formato legible
 * @param {string} dateString - Fecha en formato ISO
 * @returns {string} - Fecha formateada
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formatea monto para mostrar en formato de moneda
 * @param {string} amount - Monto como string
 * @returns {string} - Monto formateado
 */
const formatAmount = (amount) => {
  if (!amount) return '-';
  return `$${parseFloat(amount).toFixed(2)}`;
};

/**
 * Obtiene el color del badge según el estado
 * @param {string} estado - Estado de la orden
 * @returns {object} - Estilos del badge
 */
const getEstadoBadge = (estado) => {
  const badges = {
    'aprobado': {
      bg: 'bg-green-900 light:bg-green-100',
      text: 'text-green-200 light:text-green-800'
    },
    'pendiente': {
      bg: 'bg-yellow-900 light:bg-yellow-100',
      text: 'text-yellow-200 light:text-yellow-800'
    },
    'rechazado': {
      bg: 'bg-red-900 light:bg-red-100',
      text: 'text-red-200 light:text-red-800'
    },
    'cancelado': {
      bg: 'bg-gray-700 light:bg-gray-200',
      text: 'text-gray-200 light:text-gray-800'
    }
  };
  
  return badges[estado] || {
    bg: 'bg-gray-700 light:bg-gray-200',
    text: 'text-gray-200 light:text-gray-800'
  };
};

// Crea helper para definir columnas de la tabla
const columnHelper = createColumnHelper();

/**
 * Componente OrdersTable
 * @param {object} props - Propiedades del componente
 * @param {Array} props.orders - Lista de órdenes a mostrar
 * @param {boolean} props.isLoading - Estado de carga
 * @param {boolean} props.isError - Estado de error
 * @param {object} props.error - Error si existe
 * @param {boolean} props.hasNextPage - Si hay más páginas
 * @param {number} props.totalCount - Total de órdenes
 * @param {string} props.searchText - Texto de búsqueda actual
 * @param {string} props.estadoFilter - Filtro de estado actual
 * @param {string} props.orderBy - Campo de ordenamiento actual
 * @param {number} props.currentOffset - Offset actual para paginación
 * @param {Function} props.onLoadNextPage - Función para cargar siguiente página
 * @param {Function} props.onSearchChange - Función para cambiar búsqueda
 * @param {Function} props.onEstadoChange - Función para cambiar filtro de estado
 * @param {Function} props.onOrderByChange - Función para cambiar ordenamiento
 * @param {Function} props.onResetPage - Función para resetear a primera página
 * @param {Function} props.onRefresh - Función para refrescar datos
 */
const OrdersTable = ({
  orders = [],
  isLoading = false,
  isError = false,
  error = null,
  hasNextPage = false,
  totalCount = 0,
  searchText = '',
  estadoFilter = '',
  orderBy = '-created_at',
  currentOffset = 0,
  onLoadNextPage,
  onSearchChange,
  onEstadoChange,
  onOrderByChange,
  onResetPage,
  onRefresh,
  onVerificarPago
}) => {
  // Estado local para manejar la verificación de pagos
  const [verificandoPago, setVerificandoPago] = useState(null);
  // Define las columnas de la tabla usando tanstack/react-table
  const columns = React.useMemo(() => [
    columnHelper.accessor('id', {
      header: 'ID',
      cell: info => info.getValue()?.slice(0, 8) + '...'
    }),
    columnHelper.accessor('user_id', {
      header: 'Usuario ID',
      cell: info => info.getValue()?.slice(0, 8) + '...' || '-'
    }),
    columnHelper.accessor(row => row.productos?.nombre, {
      header: 'Producto',
      cell: info => info.getValue() || '-'
    }),
    columnHelper.accessor('cantidad', {
      header: 'Cantidad',
      cell: info => info.getValue() || 1
    }),
    columnHelper.accessor('monto', {
      header: 'Monto Total',
      cell: info => formatAmount(info.getValue())
    }),
    columnHelper.accessor('estado', {
      header: 'Estado',
      cell: info => {
        const estado = info.getValue();
        const badge = getEstadoBadge(estado);
        return (
          <span className={`px-2 py-1 text-xs rounded ${badge.bg} ${badge.text}`}>
            {estado || '-'}
          </span>
        );
      }
    }),
    columnHelper.accessor('mp_status', {
      header: 'Estado MP',
      cell: info => info.getValue() || '-'
    }),
    columnHelper.accessor('mp_payment_id', {
      header: 'Payment ID',
      cell: info => info.getValue() || '-'
    }),
    columnHelper.accessor('created_at', {
      header: 'Fecha',
      cell: info => formatDate(info.getValue())
    }),
    columnHelper.display({
      id: 'acciones',
      header: 'Acciones',
      cell: info => {
        const row = info.row.original;
        const esPendiente = row.estado === 'pendiente';
        const tienePaymentId = row.mp_payment_id || row.mp_preference_id;
        const estaVerificando = verificandoPago === row.id;
        
        return (
          <div className="flex gap-2">
            {/* Botón de verificación de pago - solo para pendientes con payment_id o preference_id */}
            {esPendiente && tienePaymentId && onVerificarPago && (
              <button
                onClick={() => onVerificarPago(row.id, row.mp_payment_id || row.mp_preference_id)}
                disabled={estaVerificando}
                className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Verificar estado en Mercado Pago"
              >
                {estaVerificando ? 'Verificando...' : 'Verificar Pago'}
              </button>
            )}
            
            <button
              className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              disabled
              title="Próximamente"
            >
              Ver
            </button>
            <button
              className="px-3 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
              disabled
              title="Próximamente"
            >
              Detalles
            </button>
          </div>
        );
      }
    })
  ], []);

  // Crea la instancia de la tabla con tanstack/react-table
  const table = useReactTable({
    data: orders,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  // Opciones de ordenamiento
  const orderByOptions = [
    { value: '-created_at', label: 'Fecha (reciente primero)' },
    { value: 'created_at', label: 'Fecha (antiguo primero)' },
    { value: '-monto', label: 'Monto (mayor primero)' },
    { value: 'monto', label: 'Monto (menor primero)' },
    { value: 'estado', label: 'Estado (A-Z)' }
  ];

  // Opciones de filtro de estado
  const estadoOptions = [
    { value: '', label: 'Todos los estados' },
    { value: 'aprobado', label: 'Aprobado' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'rechazado', label: 'Rechazado' },
    { value: 'cancelado', label: 'Cancelado' }
  ];

  // Manejo de cambio en búsqueda
  const handleSearchInputChange = (e) => {
    onSearchChange(e.target.value);
  };

  // Manejo de cambio en ordenamiento
  const handleOrderByChange = (e) => {
    onOrderByChange(e.target.value);
  };

  // Manejo de cambio en filtro de estado
  const handleEstadoChange = (e) => {
    onEstadoChange(e.target.value);
  };

  return (
    <div>
      {/* Header con controles de búsqueda y filtrado */}
      <div className="mb-6 flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex-1 w-full lg:w-auto">
          <input
            type="text"
            placeholder="Buscar por email, nombre o producto..."
            value={searchText}
            onChange={handleSearchInputChange}
            className="w-full px-4 py-2 border border-gray-600 light:border-gray-300 bg-gray-800 light:bg-white text-gray-100 light:text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 light:focus:ring-blue-500"
          />
        </div>
        
        <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="px-4 py-2 bg-gray-700 light:bg-gray-200 text-gray-300 light:text-gray-700 rounded-lg hover:bg-gray-600 light:hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            title="Refrescar datos"
          >
            <RiRefreshLine size={16} />
            Refrescar
          </button>
          
          <select
            value={estadoFilter}
            onChange={handleEstadoChange}
            className="px-4 py-2 border border-gray-600 light:border-gray-300 bg-gray-800 light:bg-white text-gray-100 light:text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 light:focus:ring-blue-500"
          >
            {estadoOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <select
            value={orderBy}
            onChange={handleOrderByChange}
            className="px-4 py-2 border border-gray-600 light:border-gray-300 bg-gray-800 light:bg-white text-gray-100 light:text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 light:focus:ring-blue-500"
          >
            {orderByOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <div className="text-sm text-gray-400 light:text-gray-600">
            Total: {totalCount.toLocaleString()} órdenes
          </div>
        </div>
      </div>

      {/* Estado de carga */}
      {isLoading && orders.length === 0 && (
        <div className="text-center py-8 text-gray-400 light:text-gray-600">
          Cargando órdenes...
        </div>
      )}

      {/* Estado de error */}
      {isError && (
        <div className="text-center py-8 text-red-400 light:text-red-600">
          Error: {error?.message || 'Error al cargar órdenes'}
        </div>
      )}

      {/* Tabla de órdenes */}
      {!isLoading && !isError && (
        <>
          {orders.length === 0 ? (
            <div className="text-center py-8 text-gray-400 light:text-gray-600">
              No se encontraron órdenes
            </div>
          ) : (
            <div className="bg-gray-900 light:bg-gray-50">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id} className="light:bg-gray-100">
                        {headerGroup.headers.map(header => (
                          <th 
                            key={header.id}
                            className="px-4 py-3 text-left text-sm font-semibold text-gray-300 light:text-gray-700 border-b border-gray-700 light:border-gray-200 whitespace-nowrap"
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row, index) => (
                      <tr 
                        key={row.id} 
                        className={index % 2 === 0 ? 'bg-gray-800 light:bg-white' : 'bg-gray-700 light:bg-gray-50'}
                      >
                        {row.getVisibleCells().map(cell => (
                          <td 
                            key={cell.id}
                            className="px-4 py-3 text-sm text-gray-200 light:text-gray-800 border-b border-gray-700 light:border-gray-200 whitespace-nowrap"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Controles de paginación */}
      {!isLoading && !isError && orders.length > 0 && (
        <div className="mt-6 flex justify-between items-center">
          <div className="text-sm text-gray-400 light:text-gray-600">
            Mostrando {orders.length} de {totalCount.toLocaleString()} órdenes
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={onResetPage}
              disabled={!searchText && !estadoFilter && currentOffset === 0}
              className="px-4 py-2 bg-gray-700 light:bg-gray-200 text-gray-300 light:text-gray-700 rounded-lg hover:bg-gray-600 light:hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Primera página
            </button>
            
            <button
              onClick={onLoadNextPage}
              disabled={!hasNextPage || isLoading}
              className="px-4 py-2 bg-blue-600 light:bg-blue-500 text-white rounded-lg hover:bg-blue-700 light:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Cargando...' : 'Siguiente'}
            </button>
          </div>
        </div>
      )}

      {/* Indicador de carga al cargar más páginas */}
      {isLoading && orders.length > 0 && (
        <div className="mt-4 text-center text-gray-400 light:text-gray-600">
          Cargando más órdenes...
        </div>
      )}
    </div>
  );
};

export default OrdersTable;
